import { motion } from 'framer-motion';
import { ArrowRight, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const QuickStart = () => {
  const steps = [
    {
      number: '01',
      title: 'First Free Consultation',
      color: '#3498DB',
      business: {
        title: 'Business Status',
        description: 'The first decision you will have to make is to choose your legal status. Several options: Mainland, free zones, offshore, integrating existing structure? We will assess the different options with you.',
      },
      family: {
        title: 'Family Relocation',
        description: 'The first decision is to evaluate your needs and expectations. We will put together a tailor made package to turn this milestone decision into a pleasant experience.',
      },
    },
    {
      number: '02',
      title: 'Setup Your Business and Settle Your Family',
      color: '#27AE60',
      business: {
        title: 'Business Setup',
        description: 'We will proceed with the legal registration of your business and residency. From this we will open your bank account, search for offices, and meet all specific needs to start expanding!',
      },
      family: {
        title: 'Family Settling-in',
        description: 'Choose the neighborhood based on your lifestyle. We will advise and search for the perfect house, schools, vehicle and do all the paperwork with service providers.',
      },
    },
    {
      number: '03',
      title: 'Define the Support You Need',
      color: '#5DADE2',
      business: {
        title: 'Business Development Support',
        description: 'Based on the status of your projects, we will propose a 360 tailor made support. Legal, financial, accounting, administrative, Human resources and much more.',
      },
      family: {
        title: 'Family Lifestyle',
        description: 'Dubai offers many facilities for your family. We provide Household Staffing Advisory services, select activities, get in touch with people. Enjoy being pampered by the SAM team!',
      },
    },
  ];

  const whatIfItems = [
    {
      title: 'Looking for business development support?',
      description: 'SAM is an incubator for talented entrepreneurs in need of support to structure a project step by step.',
      link: '/sam-incubator',
      linkText: 'SAM Incubator',
      color: '#3498DB'
    },
    {
      title: 'Looking for an experienced freelancer?',
      description: 'SAM brings together qualified professionals to propose ALL IN ONE activities for adults and kids.',
      link: '/sam-talents',
      linkText: 'SAM Talents',
      color: '#27AE60'
    },
    {
      title: 'Looking for a one stop solution?',
      description: 'SAM Solutions provides project management for relocation, fit out, marketing and corporate events.',
      link: '/sam-solutions',
      linkText: 'SAM Solutions',
      color: '#5DADE2'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="quick-start-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center text-white pt-20" data-testid="quick-start-hero">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920" 
            alt="Dubai"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-20 h-[1px] bg-gradient-to-r from-transparent to-[#5DADE2]" />
              <span className="text-[#5DADE2] font-semibold tracking-[0.2em] uppercase text-sm">Getting Started</span>
              <div className="w-20 h-[1px] bg-gradient-to-l from-transparent to-[#5DADE2]" />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6" data-testid="quick-start-hero-title">
              Quick <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5DADE2] to-[#27AE60]">Start Guide</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Follow these 3 easy steps to start our partnership!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="steps-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="space-y-16"
          >
            {steps.map((step, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                data-testid={`step-${index}`}
              >
                {/* Step Header */}
                <div className="flex items-center gap-6 mb-8">
                  <motion.div 
                    className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-bold"
                    style={{ background: `linear-gradient(135deg, ${step.color}, ${step.color}80)` }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    {step.number}
                  </motion.div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white">
                    {step.title}
                  </h2>
                </div>

                {/* Business & Family Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8 group hover:border-[#3498DB]/30 transition-all duration-500">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-[#3498DB]/10 rounded-full blur-3xl" />
                    <h3 className="text-2xl font-bold mb-4 text-[#3498DB]">
                      {step.business.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed relative z-10">
                      {step.business.description}
                    </p>
                  </div>

                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8 group hover:border-[#27AE60]/30 transition-all duration-500">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-[#27AE60]/10 rounded-full blur-3xl" />
                    <h3 className="text-2xl font-bold mb-4 text-[#27AE60]">
                      {step.family.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed relative z-10">
                      {step.family.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 flex flex-col md:flex-row gap-6 justify-center"
          >
            <Link 
              to="/services" 
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#3498DB] to-[#2980B9] text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(52,152,219,0.4)] transition-all duration-300"
            >
              Business Setup Services
              <ArrowRight size={20} />
            </Link>
            <Link 
              to="/services" 
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#27AE60] to-[#1E8449] text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(39,174,96,0.4)] transition-all duration-300"
            >
              Family Relocation Package
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What If Section */}
      <section className="py-24 bg-gradient-to-b from-[#0a0a0a] to-[#111]" data-testid="what-if-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              What <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">if</span>?
            </h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {whatIfItems.map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group"
              >
                <Link to={item.link} className="block h-full">
                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8 h-full hover:border-white/30 transition-all duration-500 group-hover:-translate-y-2">
                    <h3 className="text-xl font-bold text-white mb-4">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 mb-6 leading-relaxed">
                      {item.description}
                    </p>
                    <div 
                      className="inline-flex items-center gap-2 font-bold transition-all duration-300 group-hover:gap-4"
                      style={{ color: item.color }}
                    >
                      {item.linkText}
                      <ArrowRight size={18} />
                    </div>
                    
                    <div 
                      className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500"
                      style={{ background: `linear-gradient(90deg, ${item.color}, transparent)` }}
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 relative overflow-hidden" data-testid="contact-cta">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#111] to-[#0a0a0a]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#3498DB] rounded-full filter blur-[200px] opacity-10" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#27AE60] rounded-full filter blur-[200px] opacity-10" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to start your journey?
            </h3>
            <p className="text-xl text-gray-400 mb-8">
              Get 100% free first consultation! Reachable 24/7
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+971563528126"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(52,152,219,0.4)] transition-all duration-300"
              >
                <Phone size={20} />
                +971 563 528 126
              </a>
              <a
                href="mailto:groupsamrelocate@gmail.com"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-white/20 text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-300"
              >
                <Mail size={20} />
                Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default QuickStart;
